import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Grid
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import apiClient from '../../networking/axiosconfig';
import { useSelector } from 'react-redux';
import BannerDialog from './add';

const BannerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: config } = useSelector((state) => state.config);
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
  

    if (config && config.API_BASE_URL) {
      fetchBannerDetails();
    }
  }, [id, config]);
  const fetchBannerDetails = async () => {
      try {
        const response = await apiClient.get(`${config.API_BASE_URL}/api/banners/${id}`);
        setBanner(response.data);
      } catch (error) {
        console.error('Error fetching banner details:', error);
        setError('Failed to fetch banner details');
      } finally {
        setLoading(false);
      }
    };
  const toggleStatus = async () => {
    try {
      await apiClient.put(`${config.API_BASE_URL}/api/banners/${banner._id}/toggle-status`);
      setBanner(prev => ({ ...prev, isActive: !prev.isActive }));
    } catch (error) {
      console.error('Error toggling banner status:', error);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await apiClient.delete(`${config.API_BASE_URL}/api/banners/${id}`);
      navigate('/banners');
    } catch (error) {
      console.error('Error deleting banner:', error);
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
    // Refresh banner data after edit
    fetchBannerDetails();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!banner) {
    return (
      <Box p={3}>
        <Typography>Banner not found</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">{banner.title}</Typography>
        </Box>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            onClick={() => setIsEditDialogOpen(true)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color={banner.isActive ? 'warning' : 'success'}
            startIcon={banner.isActive ? <VisibilityOffIcon /> : <VisibilityIcon />}
            onClick={toggleStatus}
            sx={{ mr: 1 }}
          >
            {banner.isActive ? 'Deactivate' : 'Activate'}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setIsDeleteDialogOpen(true)}
          >
            Delete
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box mb={3} textAlign="center">
          <img
            src={banner.image}
            alt={banner.title}
            style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain' }}
          />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Banner Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary">
                Title
              </Typography>
              <Typography variant="body1">{banner.title}</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary">
                Status
              </Typography>
              <Chip
                label={banner.isActive ? 'Active' : 'Inactive'}
                color={banner.isActive ? 'success' : 'default'}
                size="small"
              />
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary">
                Link URL
              </Typography>
              <Typography variant="body1">
                {banner.link ? (
                  <a 
                    href={banner.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: 'inherit' }}
                  >
                    {banner.link}
                  </a>
                ) : 'N/A'}
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary">
                Link Target
              </Typography>
              <Typography variant="body1">
                {banner.target === '_blank' ? 'Open in new tab' : 'Open in same tab'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Additional Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary">
                Created At
              </Typography>
              <Typography variant="body1">
                {new Date(banner.createdAt).toLocaleString()}
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" color="textSecondary">
                Last Updated
              </Typography>
              <Typography variant="body1">
                {new Date(banner.updatedAt).toLocaleString()}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Edit Dialog */}
      <BannerDialog
        open={isEditDialogOpen}
        onClose={handleEditDialogClose}
        onBannerUpdated={handleEditDialogClose}
        initialBanner={banner}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => !isDeleting && setIsDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this banner? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setIsDeleteDialogOpen(false)} 
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : null}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BannerDetails;
